import { addDays, endOfMonth, format, startOfMonth } from "date-fns";

export type ReportPreset =
  | "today"
  | "last7Days"
  | "last30Days"
  | "currentMonth";
export type ReportGranularity = "day" | "month";

export type ReportPeriodParams = {
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  granularity: ReportGranularity;
};

export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function resolvePresetRange(
  preset: ReportPreset,
  referenceDate = new Date(),
): { startDate: Date; endDate: Date; granularity: ReportGranularity } {
  const normalizedReference = new Date(referenceDate);

  switch (preset) {
    case "today":
      return {
        startDate: normalizedReference,
        endDate: normalizedReference,
        granularity: "day",
      };
    case "last7Days":
      return {
        startDate: addDays(normalizedReference, -6),
        endDate: normalizedReference,
        granularity: "day",
      };
    case "last30Days":
      return {
        startDate: addDays(normalizedReference, -29),
        endDate: normalizedReference,
        granularity: "day",
      };
    case "currentMonth":
    default:
      return {
        startDate: startOfMonth(normalizedReference),
        endDate: endOfMonth(normalizedReference),
        granularity: "month",
      };
  }
}

export function formatDateForReport(value: Date) {
  return format(value, "yyyy-MM-dd");
}

export function toReportPeriodQuery({
  startDate,
  endDate,
  timezone,
  granularity,
}: ReportPeriodParams) {
  return {
    start: startDate ? formatDateForReport(startDate) : undefined,
    end: endDate ? formatDateForReport(endDate) : undefined,
    tz: timezone,
    granularity,
  };
}

export function clampYear(
  year: number,
  { min = 1970, max = 2100 }: { min?: number; max?: number } = {},
) {
  if (!Number.isInteger(year)) return new Date().getFullYear();
  if (year < min) return min;
  if (year > max) return max;
  return year;
}

export function clampMonth(month: number) {
  if (!Number.isInteger(month)) return new Date().getMonth() + 1;
  if (month < 1) return 1;
  if (month > 12) return 12;
  return month;
}
